// PATH: src/components/trials/TrialEstimateView.tsx
import * as React from "react";
import {
  Alert,
  Box,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { TrialEstimate } from "../../models/domain";
import { ROLE_LABELS, basisLabel, formatCount, formatUsd } from "./trialLogic";

interface Props {
  estimate: TrialEstimate | null;
  loading: boolean;
  error: string | null;
}

/**
 * Étape 3 : ce que l'essai va coûter. Objectifs × appels par rôle × jetons
 * moyens mesurés sur 14 jours × prix du modèle ; la base de la mesure est
 * affichée, parce qu'une moyenne « tous modèles » est moins fiable.
 */
export const TrialEstimateView: React.FC<Props> = ({ estimate, loading, error }) => (
  <Box>
    {loading && <LinearProgress sx={{ mb: 1 }} />}
    {error && (
      <Alert severity="error" sx={{ mb: 1.5 }}>
        {error}
      </Alert>
    )}
    {estimate && (
      <Stack spacing={1.5} sx={{ opacity: loading ? 0.6 : 1 }}>
        <Typography variant="body2">
          <b>{formatCount(estimate.objectives)}</b> objectif{estimate.objectives > 1 ? "s" : ""} dans{" "}
          {estimate.documents.length} document{estimate.documents.length > 1 ? "s" : ""}
          {estimate.documents.length > 1 && (
            <Typography component="span" variant="body2" color="text.secondary">
              {" "}
              ({estimate.documents.map((d) => `${d.title} : ${d.objectives}`).join(" · ")})
            </Typography>
          )}
        </Typography>
        {estimate.objectives === 0 && (
          <Alert severity="warning">
            Aucun objectif dans ces documents : l'essai n'aurait rien à extraire. Extrayez d'abord les objectifs depuis
            l'écran Chapitres.
          </Alert>
        )}
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Rôle</TableCell>
                <TableCell>Modèle</TableCell>
                <TableCell align="right">Appels</TableCell>
                <TableCell align="right">Jetons moyens (entrée · dont cache · sortie)</TableCell>
                <TableCell>Base de la mesure</TableCell>
                <TableCell align="right">Coût</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estimate.roles.map((r) => (
                <TableRow key={r.role}>
                  <TableCell>{ROLE_LABELS[r.role] ?? r.role}</TableCell>
                  <TableCell className="mono">{r.model}</TableCell>
                  <TableCell align="right">{formatCount(r.calls)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {formatCount(r.avg_input_tokens)} · {formatCount(r.avg_cached_input_tokens)} ·{" "}
                    {formatCount(r.avg_output_tokens)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color={r.basis === "none" ? "error" : "text.secondary"}>
                      {basisLabel(r)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatUsd(r.usd)}</TableCell>
                </TableRow>
              ))}
              {!estimate.roles.some((r) => r.role === "fact_review") && (
                <TableRow>
                  <TableCell>{ROLE_LABELS.fact_review}</TableCell>
                  <TableCell colSpan={5}>
                    <Typography variant="caption" color="text.secondary">
                      Coupée pour l'essai : aucun appel.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              <TableRow sx={{ "& td": { fontWeight: 650, borderBottom: 0 } }}>
                <TableCell colSpan={5}>Total estimé</TableCell>
                <TableCell align="right">{formatUsd(estimate.estimate_usd)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {estimate.estimate_usd === null && (
          <Alert severity="warning">
            Total non chiffrable : une mesure de jetons ou un tarif manque pour un des rôles. L'essai peut être lancé,
            son coût réel sera relevé à la fin.
          </Alert>
        )}
      </Stack>
    )}
  </Box>
);

export default TrialEstimateView;
